import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiEdit,
  FiPlus,
  FiEye,
  FiLock,
  FiUnlock,
  FiActivity,
  FiTrendingUp,
  FiUserCheck,
  FiAlertTriangle,
  FiKey
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useTokenIssuer, ISSUER_STATUS } from '../../contexts/TokenIssuerContext';
import { getStoredSession } from '../../api/authStorage';
import { formatSectorLabel, TOKENIZER_PLATFORM_SECTORS } from '../../api/smartTokenizerMappers';
import IssuerCreationWizard from './IssuerCreationWizard';
import IssuerDetailsModal from './IssuerDetailsModal';

// Styled Components — aligned with Dashboard page shell (gradient, glass panels, stat cards)
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
  color: #fff;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 3rem;
  padding: 4rem 3rem;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #4facfe, #00f2fe, #4facfe);
    animation: tokenizerHeroShimmer 3s infinite linear;
  }

  @keyframes tokenizerHeroShimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  h1 {
    font-size: 3.5rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    font-size: 1.3rem;
    color: #a0a0a0;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.7;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;

  .stat-icon {
    font-size: 2.5rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .stat-value {
    font-size: 2.5rem;
    font-weight: 700;
    color: #4facfe;
    margin-bottom: 0.5rem;
  }

  .stat-label {
    color: #a0a0a0;
    font-size: 0.95rem;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 20px 40px rgba(79, 172, 254, 0.1);
  }
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchAndFilters = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  position: relative;

  input {
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    background: rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    color: #fff;
    width: 100%;
    max-width: 320px;

    &:focus {
      border-color: #4facfe;
      outline: none;
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #4facfe;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #fff;

  &:focus {
    border-color: #4facfe;
    outline: none;
  }

  option {
    background: #1a1a3e;
    color: #fff;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: #fff;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.25);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 15px 40px rgba(79, 172, 254, 0.35);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.06);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.12);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(79, 172, 254, 0.35);
    }
  }
`;

const IssuersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const IssuerCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 20px 40px rgba(79, 172, 254, 0.1);
  }

  .issuer-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1rem;
  }

  .issuer-info {
    flex: 1;
  }

  .issuer-name {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.25rem;
  }

  .issuer-type {
    color: #4facfe;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .issuer-email {
    color: #a0a0a0;
    font-size: 0.85rem;
  }

  .issuer-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .issuer-actions {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn {
    padding: 0.5rem;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 6px;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(79, 172, 254, 0.3);
    }

    &.danger {
      &:hover {
        background: rgba(255, 107, 107, 0.3);
      }
    }
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.status) {
      case ISSUER_STATUS.ACTIVE:
        return `
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
          border: 1px solid rgba(34, 197, 94, 0.3);
        `;
      case ISSUER_STATUS.PENDING_VERIFICATION:
        return `
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.3);
        `;
      case ISSUER_STATUS.SUSPENDED:
        return `
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        `;
      case ISSUER_STATUS.REVOKED:
        return `
          background: rgba(127, 29, 29, 0.2);
          color: #991b1b;
          border: 1px solid rgba(127, 29, 29, 0.3);
        `;
      case ISSUER_STATUS.UNDER_REVIEW:
        return `
          background: rgba(79, 172, 254, 0.2);
          color: #4facfe;
          border: 1px solid rgba(79, 172, 254, 0.3);
        `;
      default:
        return `
          background: rgba(107, 114, 128, 0.2);
          color: #6b7280;
          border: 1px solid rgba(107, 114, 128, 0.3);
        `;
    }
  }}
`;

const IssuerDetails = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;

    .label {
      color: #a0a0a0;
    }

    .value {
      color: #fff;
      font-weight: 500;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #a0a0a0;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;

  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    color: #4facfe;
  }

  .empty-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #fff;
  }

  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto;
  }
`;

const NoticeBar = styled.div`
  background: rgba(79, 172, 254, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(79, 172, 254, 0.28);
  color: #bae6fd;
  padding: 1rem 1.25rem;
  border-radius: 15px;
  margin-bottom: 1.5rem;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const ErrorBar = styled(NoticeBar)`
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.3);
  color: #fecaca;
`;

const SectorTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const SectorPill = styled.span`
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  background: rgba(79, 172, 254, 0.12);
  border: 1px solid rgba(79, 172, 254, 0.35);
  color: #9bd6ff;
`;

const TokenIssuerManager = () => {
  const {
    issuers,
    statistics,
    error,
    updateIssuer,
    isAdmin,
    loadIssuers
  } = useTokenIssuer();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [showCreationWizard, setShowCreationWizard] = useState(false);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const session = getStoredSession();
  const hasSession = Boolean(session?.accessToken);

  const filteredIssuers = issuers.filter((issuer) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q ||
      issuer.name.toLowerCase().includes(q) ||
      (issuer.registrationNumber || '').toLowerCase().includes(q) ||
      (issuer.walletAddress || '').toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || issuer.status === statusFilter;
    const matchesSector =
      sectorFilter === 'all' || (issuer.allowedCategories || []).includes(sectorFilter);

    return matchesSearch && matchesStatus && matchesSector;
  });

  const handleStatusToggle = async (issuer) => {
    if (!isAdmin) {
      toast.error('Only administrators can change tokenizer status.');
      return;
    }

    let nextStatus;
    if (issuer.status === ISSUER_STATUS.ACTIVE) {
      nextStatus = ISSUER_STATUS.SUSPENDED;
    } else if (issuer.status === ISSUER_STATUS.SUSPENDED || issuer.status === ISSUER_STATUS.PENDING_VERIFICATION) {
      nextStatus = ISSUER_STATUS.ACTIVE;
    } else {
      nextStatus = ISSUER_STATUS.ACTIVE;
    }

    const result = await updateIssuer(issuer.id, { status: nextStatus });
    if (!result.success) {
      toast.error(result.error || 'Update failed');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case ISSUER_STATUS.ACTIVE:
        return <FiCheckCircle />;
      case ISSUER_STATUS.SUSPENDED:
        return <FiXCircle />;
      case ISSUER_STATUS.PENDING_VERIFICATION:
        return <FiActivity />;
      case ISSUER_STATUS.UNDER_REVIEW:
        return <FiEye />;
      case ISSUER_STATUS.REVOKED:
        return <FiLock />;
      default:
        return <FiAlertTriangle />;
    }
  };

  return (
    <Container>
      <HeroSection>
        <h1>Authorized tokenizers</h1>
        <p>
          View and administer backend authorized tokenizers (list, detail, create, update, approve, suspend). Operations
          that change data require an Admin account; all signed-in users can list and open details.
        </p>
      </HeroSection>

      {!hasSession && (
        <NoticeBar>
          <FiKey style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
          Sign in with Issuer Login to load data from <code>/api/authorized-tokenizers</code>.
        </NoticeBar>
      )}

      {hasSession && !isAdmin && (
        <NoticeBar>
          You are signed in without the Admin role. You can browse tokenizers and open details, but create, edit,
          approve, and suspend require an administrator.
        </NoticeBar>
      )}

      {error && <ErrorBar>{error}</ErrorBar>}

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <FiUsers className="stat-icon" />
          <div className="stat-value">{statistics.totalIssuers}</div>
          <div className="stat-label">Total Issuers</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <FiUserCheck className="stat-icon" />
          <div className="stat-value">{statistics.activeIssuers}</div>
          <div className="stat-label">Active Issuers</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <FiTrendingUp className="stat-icon" />
          <div className="stat-value">{statistics.totalIssuances}</div>
          <div className="stat-label">Total Issuances</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <FiActivity className="stat-icon" />
          <div className="stat-value">{statistics.pendingVerifications}</div>
          <div className="stat-label">Pending Verification</div>
        </StatCard>
      </StatsGrid>

      <ControlsSection>
        <SearchAndFilters>
          <SearchBox>
            <FiUsers className="search-icon" />
            <input
              type="text"
              placeholder="Search issuers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>

          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value={ISSUER_STATUS.ACTIVE}>Active</option>
            <option value={ISSUER_STATUS.PENDING_VERIFICATION}>Pending</option>
            <option value={ISSUER_STATUS.SUSPENDED}>Suspended</option>
            <option value={ISSUER_STATUS.UNDER_REVIEW}>Under Review</option>
            <option value={ISSUER_STATUS.REVOKED}>Revoked</option>
          </FilterSelect>

          <FilterSelect value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)}>
            <option value="all">All sectors</option>
            {TOKENIZER_PLATFORM_SECTORS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </FilterSelect>
          <Button className="secondary" type="button" onClick={() => loadIssuers()}>
            Refresh list
          </Button>
        </SearchAndFilters>

        <ActionButtons>
          {isAdmin && (
            <Button
              className="primary"
              type="button"
              onClick={() => {
                setSelectedIssuer(null);
                setShowCreationWizard(true);
              }}
            >
              <FiPlus />
              Register tokenizer
            </Button>
          )}
        </ActionButtons>
      </ControlsSection>

      {filteredIssuers.length === 0 ? (
        <EmptyState>
          <FiUsers className="empty-icon" />
          <div className="empty-title">No Issuers Found</div>
          <div className="empty-description">
            {searchTerm || statusFilter !== 'all' || sectorFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : hasSession
                ? isAdmin
                  ? 'Register the first authorized tokenizer or adjust filters'
                  : 'No tokenizers returned for your account'
                : 'Sign in to load authorized tokenizers from the API'}
          </div>
        </EmptyState>
      ) : (
        <IssuersGrid>
          <AnimatePresence>
            {filteredIssuers.map((issuer, index) => (
              <IssuerCard
                key={issuer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="issuer-header">
                  <div className="issuer-info">
                    <div className="issuer-name">{issuer.name}</div>
                    <div className="issuer-type">Reg: {issuer.registrationNumber || '—'}</div>
                    <div className="issuer-email" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {issuer.walletAddress || 'No wallet'}
                    </div>
                    <SectorTags>
                      {(issuer.sectors || []).map((s) => (
                        <SectorPill key={s}>{formatSectorLabel(s)}</SectorPill>
                      ))}
                    </SectorTags>
                  </div>
                  <div className="issuer-status">
                    <StatusBadge status={issuer.status}>
                      {getStatusIcon(issuer.status)}
                      {issuer.status.replace('_', ' ')}
                    </StatusBadge>
                  </div>
                </div>

                <IssuerDetails>
                  <div className="detail-row">
                    <span className="label">Sectors:</span>
                    <span className="value">{(issuer.sectors || []).length}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Linked assets (count):</span>
                    <span className="value">{issuer.totalIssuances || 0}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Created:</span>
                    <span className="value">{new Date(issuer.createdAt).toLocaleDateString()}</span>
                  </div>
                </IssuerDetails>

                <div className="issuer-actions" style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="action-btn"
                    onClick={() => {
                      setSelectedIssuer(issuer);
                      setShowDetails(true);
                    }}
                    title="View details (GET by id)"
                  >
                    <FiEye />
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        className="action-btn"
                        onClick={() => handleStatusToggle(issuer)}
                        title={
                          issuer.status === ISSUER_STATUS.ACTIVE
                            ? 'Suspend'
                            : issuer.status === ISSUER_STATUS.PENDING_VERIFICATION
                              ? 'Approve'
                              : 'Re-approve / activate'
                        }
                      >
                        {issuer.status === ISSUER_STATUS.ACTIVE ? <FiLock /> : <FiUnlock />}
                      </button>
                      <button
                        type="button"
                        className="action-btn"
                        onClick={() => {
                          setSelectedIssuer(issuer);
                          setShowCreationWizard(true);
                        }}
                        title="Edit (PUT)"
                      >
                        <FiEdit />
                      </button>
                    </>
                  )}
                </div>
              </IssuerCard>
            ))}
          </AnimatePresence>
        </IssuersGrid>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showCreationWizard && (
          <IssuerCreationWizard
            isOpen={showCreationWizard}
            onClose={() => {
              setShowCreationWizard(false);
              setSelectedIssuer(null);
            }}
            editIssuer={selectedIssuer}
          />
        )}

        {showDetails && selectedIssuer && (
          <IssuerDetailsModal
            isOpen={showDetails}
            onClose={() => {
              setShowDetails(false);
              setSelectedIssuer(null);
            }}
            issuer={selectedIssuer}
            onRequestEdit={(issuerRecord) => {
              setShowDetails(false);
              setSelectedIssuer(issuerRecord);
              setShowCreationWizard(true);
            }}
          />
        )}
      </AnimatePresence>
    </Container>
  );
};

export default TokenIssuerManager; 